import { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export const CopyTextButton = ({
  textToCopy,
  tooltipText = "Copy",
  sectionTitle,
}) => {
  const [copied, setCopied] = useState(false);

  const computeText = () => {
    if (!textToCopy && textToCopy !== 0) return "";

    // If it's already a string, use it directly
    if (typeof textToCopy === "string") {
      return sectionTitle ? `${sectionTitle}\n\n${textToCopy}` : textToCopy;
    }

    // For objects/arrays, extract text with helper
    try {
      const extracted = fullTextToCopy(textToCopy);
      return sectionTitle ? `${sectionTitle}\n\n${extracted}` : extracted;
    } catch (err) {
      // Fallback to string conversion
      const fallback = String(textToCopy);
      return sectionTitle ? `${sectionTitle}\n\n${fallback}` : fallback;
    }
  };

  const handleCopy = async () => {
    try {
      const text = computeText();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy text", error);
    }
  };
  return (
    <Tooltip title={copied ? "Copied" : tooltipText}>
      <IconButton className="custom-icon-button" onClick={handleCopy}>
        <ContentCopyIcon />
      </IconButton>
    </Tooltip>
  );
};

const fullTextToCopy = (...sections) => {
  const extractFromObject = (obj, isTopLevel = true) => {
    if (!obj) return [];
    if (typeof obj === "string") return [obj];

    if (Array.isArray(obj)) {
      if (isTopLevel) {
        // Process each item separately
        return obj.flatMap((item) => extractFromObject(item, false));
      } else {
        return obj.map((item) => {
          if (typeof item === "string") return item;
          if (typeof item === "object" && item.text) return item.text;
          return String(item);
        });
      }
    }

    if (typeof obj === "object") {
      const result = [];

      // Extract main title/heading
      const mainTitle = obj.title || obj.heading || obj.name;
      if (mainTitle && isTopLevel) {
        result.push(mainTitle);
      }

      // Extract main content/text
      const mainContent = obj.content || obj.mainText || obj.text;
      if (mainContent) {
        result.push(mainContent);
      }

      // Handle Porter's Five Forces metrics
      if (obj.metrics && typeof obj.metrics === "object") {
        result.push(""); // Blank line before metrics
        const metricsText = Object.entries(obj.metrics).map(([key, value]) => {
          const metricTitle = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
          const score = value.value || 0;
          const label = value.label || "";
          return `${metricTitle}: ${score}/5 (${label})`;
        });
        result.push(...metricsText);
      }

      // Handle sections array - Add blank line ONLY before section title
      if (obj.sections && Array.isArray(obj.sections)) {
        obj.sections.forEach((section) => {
          // Add blank line before section title
          result.push("");

          // Add section number if exists (Porter's)
          if (section.number) {
            result.push(`${section.number}. ${section.title || ""}`);
            result.push("");
          } else if (section.title) {
            result.push(section.title);
            result.push("");
          }

          // Add rating if exists (Porter's)
          if (section.rating) {
            result.push(`Rating: ${section.rating}/5`);
          }

          // Add section content if exists
          if (section.content || section.text) {
            result.push("");
            result.push(section.content || section.text);
          }

          if (section.bullets && Array.isArray(section.bullets)) {
            section.bullets.forEach((bullet) => {
              result.push(bullet);
            });
          }
        });
      }

      // Handle Porter's analysis object separately
      if (obj.analysis && typeof obj.analysis === "object") {
        const analysisContent = extractFromObject(obj.analysis, false);
        result.push(...analysisContent);
      }

      // Handle Further Reading
      if (
        obj.furtherReading &&
        Array.isArray(obj.furtherReading) &&
        obj.furtherReading.length > 0
      ) {
        result.push("");
        result.push("Further Reading");
        result.push("");
        obj.furtherReading.forEach((item) => {
          result.push(item);
        });
      }

      // Handle generic lists/items/bullets (fallback)
      const genericList = obj.list || obj.items || obj.bullets;
      if (
        genericList &&
        Array.isArray(genericList) &&
        !obj.sections &&
        !obj.furtherReading
      ) {
        genericList.forEach((item) => {
          if (typeof item === "string") {
            result.push(item);
          } else if (typeof item === "object") {
            result.push(...extractFromObject(item, false));
          }
        });
      }

      return result;
    }

    return [];
  };

  const allContent = sections
    .flatMap((s) => extractFromObject(s, true))
    .filter((item) => item !== null && item !== undefined);

  return allContent.join("\n");
};
